import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import styles from "./SignupModal.module.css";
import CONFIG from "config/config";
import { createClientFromPlatform } from "graphQL/mutations/signup";
import { claimGuestDesign } from "graphQL/mutations/claimGuestDesign";
import { isExistUserWithEmail } from "graphQL/query/signupChecks";
import { Country, getCountries } from "graphQL/query/countries";
import { userAuth } from "graphQL/query/userAuth";
import { authorization } from "module/auth";
import { decrypt } from "pages/auth/utils";
import { meRequestInit, meUrl } from "module/api/me";
import { BUILDER_OAUTH_HANDOVER_KEY } from "pages/auth/components/oauth-callback";
import Captcha, { isCaptchaRequired } from "./Captcha";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Format of the flyer the guest is currently editing (TEARAWAY, A6, etc.).
   * Forwarded to `claimGuestDesign` so the new CampaignDesign row inherits it.
   */
  currentFormat?: string;
  /**
   * Guest's chosen design name from the toolbar's DesignNameInput, if any.
   */
  currentDesignName?: string;
};

const ROLE_CLIENT_ADMIN = "CLIENT_ADMIN";
const SOURCE_TAG = "campaign-builder-guest";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ONLY_LETTERS = /^[A-Za-zÀ-ÿ\s'-]+$/;

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32">
    <path
      fill="#4285F4"
      d="M30.9 16.36c0-1.13-.1-2.22-.29-3.27H16v6.18h8.38c-.36 1.95-1.46 3.6-3.11 4.7v3.91h5.03c2.94-2.71 4.64-6.7 4.64-11.52z"
    />
    <path
      fill="#34A853"
      d="M16 31.5c4.2 0 7.72-1.39 10.3-3.78l-5.03-3.91c-1.4.94-3.18 1.49-5.27 1.49-4.05 0-7.48-2.74-8.7-6.42H2.11v4.03A15.5 15.5 0 0 0 16 31.5z"
    />
    <path
      fill="#FBBC05"
      d="M7.3 18.88A9.33 9.33 0 0 1 6.82 16c0-.99.17-1.95.48-2.88V9.09H2.11A15.5 15.5 0 0 0 .5 16c0 2.51.6 4.88 1.61 6.91l4.03-3.13 1.16-1z"
    />
    <path
      fill="#EA4335"
      d="M16 6.7c2.28 0 4.33.78 5.94 2.32l4.46-4.46C23.7 2.12 20.18.5 16 .5A15.5 15.5 0 0 0 2.11 9.09l5.19 4.03C8.52 9.44 11.95 6.7 16 6.7z"
    />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const benefits = [
  {
    key: "b1",
    icon: (
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="13.5" cy="6.5" r=".5" fill="rgba(255,255,255,0.85)" />
        <circle cx="17.5" cy="10.5" r=".5" fill="rgba(255,255,255,0.85)" />
        <circle cx="8.5" cy="7.5" r=".5" fill="rgba(255,255,255,0.85)" />
        <circle cx="6.5" cy="12.5" r=".5" fill="rgba(255,255,255,0.85)" />
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
      </svg>
    ),
    text: (
      <>
        <strong>Brand Kit</strong> &mdash; colors, fonts &amp; logo in one click
      </>
    ),
    alignStart: false,
  },
  {
    key: "b2",
    icon: (
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1" />
        <path d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" />
      </svg>
    ),
    text: (
      <>
        <strong>Smart Variables</strong> &mdash; set once, personalize thousands
      </>
    ),
    // Center the icon vertically with the (potentially two-line) text.
    alignStart: false,
  },
];

type FormState = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  countryId: number | "";
  phone: string;
  companyName: string;
};

type Errors = Partial<Record<keyof FormState, string>>;

const INITIAL_FORM: FormState = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  countryId: "",
  phone: "",
  companyName: "",
};

/**
 * Pick a sensible default country for the dropdown.
 *
 * Reads the user's browser locale (`navigator.language` →
 * `Intl.Locale.region`), then looks for that ISO 3166-1 alpha-2 code
 * in the loaded country list. If the region matches a country on the
 * allow-list (e.g. `en-GB` → United Kingdom, `es-ES` → Spain), returns
 * that country's id. Otherwise — region missing, detection threw, or
 * region not in our supported markets — falls back to the United
 * States. Returns null only if neither the detected country nor the
 * US-fallback are in the list (highly unlikely; would mean `getCountries`
 * returned an unexpected payload).
 *
 * Pure function, no React state. Caller owns the side effect.
 */
const detectDefaultCountryId = (list: Country[]): number | null => {
  let regionCode: string | undefined;
  try {
    const userLocale = navigator.language || navigator.languages?.[0] || "en-US";
    regionCode = new Intl.Locale(userLocale).region?.toUpperCase();
  } catch {
    /* malformed locale or no Intl.Locale — fall through to US default */
  }
  const matchByCode = (code?: string) => (code ? list.find((c) => c.code?.toUpperCase() === code) : undefined);
  const detected = matchByCode(regionCode);
  if (detected) return detected.id;
  // Fallback: United States, either by code (US) or by name as a
  // secondary safety net if the backend ever omits the code field.
  const us = matchByCode("US") || list.find((c) => c.name === "United States");
  return us ? us.id : null;
};

/**
 * Custom dropdown that visually matches the rest of the signup form
 * (`fmInput`-styled trigger, chevron, themed option panel) instead of
 * the browser-default `<select>` popup. Closes on outside click and on
 * Escape. Keyboard support is minimal — Enter from inside the modal
 * still submits the parent `<form>`, so we deliberately don't trap it
 * here.
 */
const CountrySelect: React.FC<{
  value: number | "";
  countries: Country[];
  onChange: (id: number | "") => void;
}> = ({ value, countries, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selected = countries.find((c) => c.id === value);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        className={`${styles.fmSelect} ${open ? styles.fmSelectOpen : ""}`}
        onClick={() => setOpen((o) => !o)}
        data-cypress-id="guest-signup-country"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "" : styles.fmSelectPlaceholder}>
          {selected ? selected.name : "Select your country"}
        </span>
        <span className={`${styles.fmSelectChevron} ${open ? styles.fmSelectChevronOpen : ""}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open && (
        <div className={styles.fmSelectPanel} role="listbox">
          {countries.map((c) => (
            <div
              key={c.id}
              role="option"
              aria-selected={c.id === value}
              className={`${styles.fmSelectOption} ${c.id === value ? styles.fmSelectOptionActive : ""}`}
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
            >
              {c.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SignupModal: React.FC<Props> = ({ isOpen, onClose, currentFormat, currentDesignName }) => {
  // The modal serves two flows: a brand-new "signup" (two-step form) and
  // "login" for users who already have an account. We share email/password
  // state between them so an existing user who started typing in signup
  // and saw "this email is already registered" can flip to login with
  // their input preserved.
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [step, setStep] = useState<1 | 2>(1);
  // Confirmation overlay shown when the user clicks the Brand
  // Ambassador link — leaving Creative Studio without registering
  // throws away the in-progress design, so we ask first.
  const [showBaWarning, setShowBaWarning] = useState(false);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Errors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [active, setActive] = useState(false);
  const [animateLeft, setAnimateLeft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  // Login-specific submit state. Kept separate from `submitting`/`submitError`
  // so the two flows don't accidentally clobber each other's UI (e.g. a stale
  // signup error showing up after a failed login retry).
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string>("");
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoaded, setCountriesLoaded] = useState(false);
  // OAuth handover state — when set, the user got here via Google instead
  // of email/password. Step 1 (email + password) is skipped, the form is
  // pre-filled, and submit goes through with `authMethod: 'SOCIAL'`.
  const [oauthData, setOauthData] = useState<{
    providerType: string;
    providerUserId: string;
  } | null>(null);
  const [oauthBusy, setOauthBusy] = useState(false);
  // Captcha gate — mirrors oppizi-platform: only enforced in prod, off
  // by default in dev/local so signup is testable without solving it.
  const [isCaptchaSuccess, setIsCaptchaSuccess] = useState(!isCaptchaRequired());
  const emailCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the `setInterval` id from `handleGoogleClick`'s popup-close
  // watcher. Parked on a ref so the cleanup effect below can clear it on
  // unmount even if the popup is still open — otherwise the interval keeps
  // firing every 500 ms and calls `setOauthBusy` on an unmounted tree.
  const oauthWatchRef = useRef<number | null>(null);

  // Clear the popup-close watcher on unmount. The happy path
  // (`popup.closed`) already self-clears inside the interval, so this only
  // matters when the user navigates away or the modal is unmounted while
  // the OAuth popup is still open.
  useEffect(() => {
    return () => {
      if (oauthWatchRef.current !== null) {
        window.clearInterval(oauthWatchRef.current);
        oauthWatchRef.current = null;
      }
    };
  }, []);

  // Reset everything when reopened.
  useEffect(() => {
    if (!isOpen) {
      setActive(false);
      setAnimateLeft(false);
      setMode("signup");
      setStep(1);
      setForm(INITIAL_FORM);
      setErrors({});
      setSubmitError("");
      setLoginError("");
      // Without these the next open would inherit OAuth state from a
      // popup the user dismissed mid-flow: `oauthData` flips Step 1
      // into the password-less variant for someone who hasn't actually
      // authed, and a stuck `oauthBusy=true` blocks all subsequent
      // Google clicks until full reload. Reset both on close to keep
      // the next session a clean slate.
      setOauthData(null);
      setOauthBusy(false);
      setSubmitting(false);
      setLoginSubmitting(false);
      setShowBaWarning(false);
      return;
    }
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => {
        setActive(true);
        setAnimateLeft(true);
      });
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [isOpen]);

  // Lazy-load the country list when the user reaches step 2. Once it
  // arrives, pre-select the user's country from their browser locale
  // (e.g. `en-GB` → United Kingdom). If the detected region isn't in
  // our allow-list — or detection fails entirely — default to the
  // United States. The user can still override via the dropdown; we
  // only set the default when `countryId` is still empty.
  useEffect(() => {
    if (step !== 2 || countriesLoaded) return;
    let cancelled = false;
    getCountries()
      .then((list) => {
        if (cancelled) return;
        setCountries(list);
        setCountriesLoaded(true);
        setForm((f) => {
          if (f.countryId !== "") return f;
          const detected = detectDefaultCountryId(list);
          return detected !== null ? { ...f, countryId: detected } : f;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCountriesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [step, countriesLoaded]);

  // Intentionally no ESC-to-close and no backdrop-click-to-close on
  // this modal — once the guest is partway through the signup form,
  // an accidental ESC or stray click outside the card would dump
  // unsaved field data. The only dismissal affordance is the
  // explicit close button (✕) in the top-right corner, same UX the
  // platform's onboarding flow uses for the same reason.

  // Email uniqueness check (debounced 500ms — same as oppizi-platform Step1.js).
  useEffect(() => {
    if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
    const email = form.email.trim();
    if (!email || !EMAIL_REGEX.test(email)) return;
    emailCheckTimer.current = setTimeout(async () => {
      try {
        const exists = await isExistUserWithEmail(email);
        if (exists) {
          setErrors((e) => ({ ...e, email: "This email is already registered" }));
        } else {
          setErrors((e) => {
            if (e.email === "This email is already registered") {
              const { email: _drop, ...rest } = e;
              return rest;
            }
            return e;
          });
        }
      } catch {
        /* network noise — leave the field as-is, the submit-time validation will catch it */
      }
    }, 500);
    return () => {
      if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
    };
  }, [form.email]);

  const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => {
      if (!(field in e)) return e;
      const { [field]: _drop, ...rest } = e;
      return rest;
    });
  };

  // Step 1: ready to advance once email passes format+uniqueness,
  // password is non-empty, and (in prod) the captcha is solved.
  //
  // OAuth exception: when `oauthData` is set the user reached Step 1
  // through Google (Step 1 normally gets skipped in that flow, but
  // the user can still land here via the Step-2 Back button). Google
  // already proved the email is theirs, no password will ever exist
  // for this account, and the captcha is meant to block password
  // brute force — none of those gates apply, so only the email check
  // remains.
  const step1Ready = useMemo(() => {
    if (!EMAIL_REGEX.test(form.email.trim())) return false;
    if (errors.email) return false;
    if (oauthData) return true;
    if (form.password.length < 6) return false;
    if (!isCaptchaSuccess) return false;
    return true;
  }, [form.email, form.password, errors.email, isCaptchaSuccess, oauthData]);

  // Step 2: every required field present + no errors. Email check is
  // kept here as a safety net — the OAuth signup path can drop the
  // user straight into Step 2, and if the provider didn't return an
  // email (observed on some oppizi-auth callbacks) we'd otherwise
  // submit garbage. Step 1 enforces the same check before advancing
  // for the regular email/password path.
  const step2Ready = useMemo(() => {
    if (!EMAIL_REGEX.test(form.email.trim())) return false;
    if (errors.email) return false;
    if (form.firstName.trim().length < 2) return false;
    if (form.lastName.trim().length < 2) return false;
    if (!form.countryId) return false;
    if (!form.phone || form.phone.replace(/\D/g, "").length < 6) return false;
    if (form.companyName.trim().length < 2) return false;
    return true;
  }, [form, errors]);

  // Login mode is "ready" with a syntactically valid email and any
  // non-empty password. We deliberately do NOT require captcha here
  // (mirrors oppizi-platform's `/login` page) and we IGNORE the
  // `errors.email` "already registered" hint — for login, that's the
  // happy path.
  const loginReady = useMemo(() => {
    if (!EMAIL_REGEX.test(form.email.trim())) return false;
    if (form.password.length < 1) return false;
    return true;
  }, [form.email, form.password]);

  const handleAdvance = () => {
    if (!step1Ready) return;
    setStep(2);
  };

  // Switch between signup/login while preserving the shared email +
  // password input. Local submit state and per-mode errors are cleared
  // so neither UI shows a stale error message from the other flow.
  const switchToLogin = () => {
    setMode("login");
    setSubmitError("");
    setLoginError("");
  };
  const switchToSignup = () => {
    setMode("signup");
    setStep(1);
    setSubmitError("");
    setLoginError("");
  };

  /**
   * Once we have a valid auth token + user (whether from email signup,
   * existing OAuth login, or new OAuth signup), run the same post-signup
   * tail: persist auth, claim the guest's in-progress design, and
   * dispatch the event Tldraw.tsx is listening to so it copies the
   * Liveblocks state and redirects to the authed builder URL.
   */
  const runPostSignupComplete = async (authToken: string, user: any, extras: { userId?: any; clientId?: any } = {}) => {
    if (user) {
      authorization.login(user, authToken);
    } else {
      try {
        window.localStorage.setItem("apiKey", authToken);
      } catch {
        /* ignore */
      }
    }

    let claimedDesignId: string | number | undefined;
    let claimedFormat: string | undefined;
    try {
      const claim = await claimGuestDesign({
        format: currentFormat,
        fileName: currentDesignName,
      });
      if (claim.success) {
        claimedDesignId = claim.designId;
        claimedFormat = claim.format;
      } else if (claim.error) {
        console.warn("[SignupModal] claimGuestDesign failed:", claim.error);
      }
    } catch (claimErr: any) {
      console.warn("[SignupModal] claimGuestDesign threw:", claimErr?.message || claimErr);
    }

    window.dispatchEvent(
      new CustomEvent("guest-signup-complete", {
        detail: {
          authToken,
          user,
          userId: extras.userId,
          clientId: extras.clientId,
          designId: claimedDesignId,
          format: claimedFormat || currentFormat,
        },
      })
    );
    onClose();
  };

  const handleSubmit = async () => {
    if (!step2Ready || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const isSocial = !!oauthData;
      const res = await createClientFromPlatform({
        authMethod: isSocial ? "SOCIAL" : "OPPIZI",
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        // OAuth flows don't carry a password — sending one would let the
        // backend reject the request as malformed.
        password: isSocial ? undefined : form.password,
        role: ROLE_CLIENT_ADMIN,
        mobilePhone: form.phone,
        companyName: form.companyName.trim(),
        countryId: Number(form.countryId),
        isSelfServeEnabled: true,
        createdBy: SOURCE_TAG,
        providerType: oauthData?.providerType,
        providerUserId: oauthData?.providerUserId,
      });
      await runPostSignupComplete(res.authToken, res.user, {
        userId: res.userId,
        clientId: res.clientId,
      });
    } catch (err: any) {
      setSubmitError(err?.message || "Signup failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Existing-user login. Hits the same `/auth` REST endpoint the platform
   * login page uses (`userAuth`), then runs the same post-signup tail
   * (`runPostSignupComplete`) so the in-progress guest design is claimed
   * by the now-authed user and the carry-over redirect fires. The
   * `guest-signup-complete` event handler in `Tldraw.tsx` is generic —
   * it doesn't care whether the auth came from signup or login, only
   * that we hand it a valid `{ authToken, user, designId }` triple.
   */
  const handleLogin = async () => {
    if (!loginReady || loginSubmitting) return;
    setLoginSubmitting(true);
    setLoginError("");
    try {
      const res = await userAuth(form.email.trim(), form.password);
      if (!res.apiKey || !res.user) {
        throw new Error("Login response missing credentials");
      }
      await runPostSignupComplete(res.apiKey, res.user, { userId: res.user.id });
    } catch (err: any) {
      // The REST endpoint returns plain text bodies for some failure
      // modes ("Invalid email or password.") — preserve them when we
      // can, fall back to a generic message otherwise.
      setLoginError(err?.message || "Login failed. Please check your credentials and try again.");
    } finally {
      setLoginSubmitting(false);
    }
  };

  /**
   * Open the Google OAuth flow in a popup window. The popup is redirected
   * by Google → oppizi-auth → back to one of our /auth/social/* routes
   * (with `source=campaign-builder`). That route posts the result back
   * here via `window.opener.postMessage`. The listener below picks it up
   * and either pre-fills Step 2 (new user) or logs in directly (existing).
   */
  const handleGoogleClick = () => {
    if (oauthBusy) return;
    setOauthBusy(true);
    setSubmitError("");

    // Random state so OAuthRequest metadata and replay protection work
    // server-side.
    const state =
      typeof crypto !== "undefined" && (crypto as any).randomUUID
        ? (crypto as any).randomUUID()
        : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const url =
      `${CONFIG.API_BASE_URL}/auth/google/authenticate` + `?state=${encodeURIComponent(state)}&source=campaign-builder`;

    const popup = window.open(url, "oppizi-google-oauth", "width=520,height=640");
    if (!popup) {
      setOauthBusy(false);
      setSubmitError("Couldn't open the Google sign-in window. Please allow popups and try again.");
      return;
    }

    // If the popup is closed without completing, unblock the button. The
    // interval id is stored on `oauthWatchRef` so the unmount cleanup
    // effect above can clear it if the modal is torn down before the
    // popup closes. We also clear any prior watcher in case the user
    // re-clicks the Google button while a previous popup is still open.
    if (oauthWatchRef.current !== null) {
      window.clearInterval(oauthWatchRef.current);
    }
    oauthWatchRef.current = window.setInterval(() => {
      if (popup.closed) {
        if (oauthWatchRef.current !== null) {
          window.clearInterval(oauthWatchRef.current);
          oauthWatchRef.current = null;
        }
        setOauthBusy(false);
      }
    }, 500);
  };

  // Listen for the popup's handover. The primary path is localStorage +
  // the `storage` event (same approach as oppizi-platform's
  // `SignUpCallback.js` — battle-tested across browsers and resilient to
  // popup-close timing and Safari's cross-site `window.opener`
  // invalidation during the Google OAuth round-trip). We also keep a
  // `message` listener as a fallback for the case where `OAuthHandover`
  // couldn't write to localStorage (private mode, disk full) and fell
  // back to postMessage on its end.
  useEffect(() => {
    if (!isOpen) return;

    const processOAuthDetail = async (detail: any) => {
      if (!detail || typeof detail !== "object") return;
      const { type } = detail;

      if (type === "oauth-social-signup") {
        // New OAuth user — pre-fill the form with what the provider
        // gave us. The intent is to skip straight to Step 2, BUT if
        // the email is missing from the OAuth callback (oppizi-auth
        // has been observed omitting the `email` param on some signup
        // redirects), we keep the user on Step 1 instead so they can
        // type it manually. Step 1's OAuth-aware `step1Ready` lets
        // them advance without a password.
        const { email, firstName, lastName, providerUserId, providerType } = detail;

        setOauthData({
          providerType: providerType || "google",
          providerUserId: providerUserId || "",
        });
        setForm((f) => ({
          ...f,
          email: email || "",
          firstName: firstName || "",
          lastName: lastName || "",
        }));
        // The Google flow can be triggered from either mode. If the user
        // hit "Continue with Google" from the login screen and Google
        // returned a brand-new account, kick the modal back into signup
        // so the Step 2 form they're about to see is actually visible.
        setMode("signup");
        if (email) {
          // Happy path — provider returned an email, jump to Step 2.
          setStep(2);
        } else {
          // Sad path — provider omitted the email. Keep the user on
          // Step 1 so the (now password-less, captcha-less) email
          // input is visible and they can enter it themselves.
          console.warn(
            "[SignupModal] OAuth signup payload missing email — staying on Step 1 for manual entry. " +
              "Check oppizi-auth's signup callback params (the popup's console logs all URL params)."
          );
          setStep(1);
        }
        setOauthBusy(false);
        return;
      }

      if (type === "oauth-social-login") {
        // Existing OAuth user — we already have a JWT (encrypted by
        // oppizi-auth). Decrypt, hydrate the user via /me, then run
        // the same claim+dispatch tail as a fresh signup. This is the
        // path that fires when the user clicks "Continue with Google"
        // (from either the signup OR login mode of the modal) and
        // the email they pick is already registered — Google proves
        // identity, oppizi-auth issues a JWT, and we hand it off
        // here exactly like a password login would. The carry-over
        // works the same way for both signup and login because the
        // `guest-signup-complete` event in `Tldraw.tsx` doesn't
        // distinguish between them.
        const { apiKeyEncrypted } = detail;
        // Surface the error in BOTH error slots so it shows up
        // regardless of which mode (signup/login) the modal is in
        // when the OAuth round-trip resolves — the listener captures
        // `mode` from a stale closure and we'd otherwise lose the
        // message to the wrong UI half the time.
        const surfaceOAuthError = (msg: string) => {
          setSubmitError(msg);
          setLoginError(msg);
        };
        if (!apiKeyEncrypted) {
          setOauthBusy(false);
          surfaceOAuthError("OAuth login failed: no token received.");
          return;
        }
        try {
          const apiKey = decrypt(apiKeyEncrypted);
          if (!apiKey) throw new Error("Couldn't decrypt OAuth token");

          // Pull the user with the key we just got (x-api-key, no cookies — PAD-33097).
          const meResponse = await fetch(meUrl(), meRequestInit(apiKey));
          if (!meResponse.ok) {
            throw new Error(`Could not load profile (${meResponse.status})`);
          }
          const meBody = await meResponse.json();
          const user = meBody?.user || meBody;
          await runPostSignupComplete(apiKey, user, { userId: user?.id });
        } catch (err: any) {
          console.warn("[SignupModal] OAuth login failed:", err?.message || err);
          surfaceOAuthError(err?.message || "OAuth login failed. Please try again.");
          setOauthBusy(false);
        }
      }
    };

    const consumeLocalStorageHandover = () => {
      try {
        const raw = localStorage.getItem(BUILDER_OAUTH_HANDOVER_KEY);
        if (!raw) return;

        let detail: any;
        try {
          detail = JSON.parse(raw);
        } catch {
          localStorage.removeItem(BUILDER_OAUTH_HANDOVER_KEY);
          return;
        }

        // Age-gate. Reject entries missing `ts` outright — those are
        // either legacy payloads from before this code shipped, a
        // same-origin script writing to this key without following
        // our schema, or a manually-crafted entry; we only want to
        // consume properly-formed handover payloads from this build's
        // `OAuthHandover`. Otherwise drop entries older than 5
        // minutes (browser crash, fast tab switch, user opened modal
        // long after a stranded OAuth attempt).
        if (!detail.ts || Date.now() - detail.ts > 5 * 60 * 1000) {
          localStorage.removeItem(BUILDER_OAUTH_HANDOVER_KEY);
          return;
        }

        // Consume — remove BEFORE processing so an async failure in
        // `processOAuthDetail` doesn't leave the payload behind to
        // trigger again on next mount.
        localStorage.removeItem(BUILDER_OAUTH_HANDOVER_KEY);

        // Defense-in-depth: login payloads (which carry the encrypted
        // apiKey) are NEVER supposed to land in localStorage — the
        // OAuthHandover writes them via postMessage only to avoid the
        // XSS exfiltration window. If something else writes a login
        // payload here, drop it on the floor instead of processing.
        if (detail.type === "oauth-social-login") return;

        processOAuthDetail(detail);
      } catch {
        /* localStorage unavailable — postMessage fallback may still fire */
      }
    };

    // Check on mount in case the popup wrote to localStorage BEFORE the
    // modal opened or this listener attached (race between popup speed
    // and React render — happens noticeably in fast-popup scenarios).
    consumeLocalStorageHandover();

    // Primary path: same-origin `storage` event fires on every other
    // tab when the popup writes. By spec, storage events do NOT fire
    // in the tab that wrote them, so this listener only sees events
    // from the popup writing — no self-trigger to worry about.
    const storageHandler = (e: StorageEvent) => {
      if (e.key !== BUILDER_OAUTH_HANDOVER_KEY || !e.newValue) return;
      consumeLocalStorageHandover();
    };
    window.addEventListener("storage", storageHandler);

    // Fallback path: postMessage. Triggered by either the popup's
    // explicit `window.opener.postMessage` (signup-when-localStorage-fails
    // or any login flow) — origin-gated to this builder's exact origin
    // to prevent forgery.
    const messageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      processOAuthDetail(event.data);
    };
    window.addEventListener("message", messageHandler);

    // Robust path for the login flow: `BroadcastChannel` survives the
    // cross-origin redirect chain that frequently nulls out
    // `window.opener` (oppizi-auth → Google → builder is a textbook
    // case where the browser severs the opener-window link). Same
    // origin only by definition of BroadcastChannel — no extra origin
    // check needed. If the runtime doesn't support it (very old
    // browsers), the postMessage handler above remains.
    let broadcastChannel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        broadcastChannel = new BroadcastChannel("oppizi-oauth-handover");
        broadcastChannel.onmessage = (e: MessageEvent) => {
          processOAuthDetail(e.data);
        };
      } catch {
        /* legacy Safari Private Mode threw on construction — fine */
      }
    }

    return () => {
      window.removeEventListener("storage", storageHandler);
      window.removeEventListener("message", messageHandler);
      if (broadcastChannel) {
        try {
          broadcastChannel.close();
        } catch {
          /* already closed */
        }
      }
    };
    // `runPostSignupComplete` and friends intentionally not in deps —
    // we'd re-attach listeners every render and miss the popup-close
    // window. Closure captures the current refs which is fine here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const overlayClass = [styles.signupModal, styles.visible, active ? styles.active : ""].filter(Boolean).join(" ");
  const leftClass = [styles.su2Left, animateLeft ? styles.animate : ""].filter(Boolean).join(" ");

  return (
    // No backdrop-click handler — see the ESC comment above. The X
    // button is the only way out. The inner `stopPropagation` is now
    // moot but kept defensively in case a future child reintroduces
    // a click-outside-closes wrapper above this layer.
    <div className={overlayClass}>
      <div className={styles.fmCard} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.fmCloseBtn}
          onClick={onClose}
          aria-label="Close"
          data-cypress-id="guest-signup-close"
        >
          ✕
        </button>

        <div className={leftClass}>
          <div className={styles.su2Deco1} />
          <div className={styles.su2Deco2} />
          <div className={styles.su2Eyebrow}>Save Your Work</div>
          <div className={styles.su2LeftTitle}>
            Your flyer is ready.
            <br />
            Keep it forever with continued access to the Creative Studio tools.
          </div>
          <div className={styles.su2Benefits}>
            {benefits.map((b) => {
              const benefitClass = [styles.su2Benefit, styles[b.key], b.alignStart ? styles.alignStart : ""]
                .filter(Boolean)
                .join(" ");
              return (
                <div key={b.key} className={benefitClass}>
                  <div className={styles.su2BIcon}>{b.icon}</div>
                  <div className={styles.su2BText}>{b.text}</div>
                </div>
              );
            })}
          </div>
          <div className={styles.su2More}>...and many more tools inside</div>
        </div>

        <div className={styles.su2Right}>
          {/* Step tracker lives BELOW each step's primary action
              button (see the two render sites further down in the
              signup branches). Login mode is single-step, so it
              doesn't render a tracker at all. */}
          {mode === "login" ? (
            <>
              <div className={styles.su2RightTitle}>Welcome back</div>
              <div className={styles.su2RightSub}>Log in to keep building</div>

              <button
                className={styles.fmGoogleBtn}
                type="button"
                onClick={handleGoogleClick}
                disabled={oauthBusy}
                data-cypress-id="guest-login-google"
              >
                <span className={styles.fmGoogleIconWrap}>
                  <GoogleIcon />
                </span>
                <span className={styles.fmGoogleLabelWrap}>
                  <span className={styles.fmGoogleLabel}>{oauthBusy ? "Opening Google…" : "Continue with Google"}</span>
                </span>
              </button>

              <div className={styles.fmOrWrap}>
                <div className={styles.fmOrLine} />
                <span className={styles.fmOrText}>OR</span>
                <div className={styles.fmOrLine} />
              </div>

              <form
                className={styles.fmSignupFields}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (loginReady && !loginSubmitting) handleLogin();
                }}
              >
                <div className={styles.fmField}>
                  <div className={styles.fmFieldLabel}>
                    Email <span className={styles.fmFieldReq}>*</span>
                  </div>
                  <div className={styles.fmInputWrap}>
                    <input
                      type="email"
                      className={styles.fmInput}
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      data-cypress-id="guest-login-email"
                      autoComplete="email"
                    />
                  </div>
                </div>
                <div className={styles.fmField}>
                  <div className={styles.fmFieldLabel}>
                    Password <span className={styles.fmFieldReq}>*</span>
                  </div>
                  <div className={styles.fmInputWrap}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className={styles.fmInput}
                      placeholder="••••••••"
                      style={{ paddingRight: 40 }}
                      value={form.password}
                      onChange={(e) => updateField("password", e.target.value)}
                      data-cypress-id="guest-login-password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className={styles.fmEyeBtn}
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      data-cypress-id="guest-login-password-toggle"
                    >
                      <EyeOffIcon />
                    </button>
                  </div>
                </div>

                {loginError && <div className={styles.fmFieldError}>{loginError}</div>}

                <button
                  type="submit"
                  className={`${styles.fmEmailCta} ${loginReady && !loginSubmitting ? styles.enabled : ""}`}
                  disabled={!loginReady || loginSubmitting}
                  data-cypress-id="guest-login-submit"
                >
                  {loginSubmitting ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <CircularProgress size={16} color="inherit" thickness={5} />
                      Logging in...
                    </span>
                  ) : (
                    "Log in"
                  )}
                </button>
              </form>

              <div className={styles.fmSignupBottom}>
                <div>
                  Don&apos;t have an account?{" "}
                  <span
                    className={styles.fmBottomLink}
                    role="button"
                    tabIndex={0}
                    onClick={switchToSignup}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        switchToSignup();
                      }
                    }}
                    data-cypress-id="guest-login-switch-to-signup"
                  >
                    Sign up.
                  </span>
                </div>
              </div>
            </>
          ) : step === 1 ? (
            <>
              <div className={styles.su2RightTitle}>{oauthData ? "Confirm your email" : "Create a free account"}</div>
              <div className={styles.su2RightSub}>
                {oauthData
                  ? "We couldn't read your email from Google — type it to continue"
                  : "No credit card required"}
              </div>

              {/* Google + OR divider only when NOT already coming from
                  OAuth. If the user reached Step 1 via the OAuth flow
                  (provider didn't return an email, or the user clicked
                  back from Step 2), re-offering Google would be
                  confusing — they've already authed. */}
              {!oauthData && (
                <>
                  <button
                    className={styles.fmGoogleBtn}
                    type="button"
                    onClick={handleGoogleClick}
                    disabled={oauthBusy}
                    data-cypress-id="guest-signup-google"
                  >
                    <span className={styles.fmGoogleIconWrap}>
                      <GoogleIcon />
                    </span>
                    <span className={styles.fmGoogleLabelWrap}>
                      <span className={styles.fmGoogleLabel}>
                        {oauthBusy ? "Opening Google…" : "Continue with Google"}
                      </span>
                    </span>
                  </button>

                  <div className={styles.fmOrWrap}>
                    <div className={styles.fmOrLine} />
                    <span className={styles.fmOrText}>OR</span>
                    <div className={styles.fmOrLine} />
                  </div>
                </>
              )}

              {/* Wrapping the email/password fields in a <form> lets the
                  browser fire the implicit submit on Enter from any
                  input — the `type="submit"` Continue button below
                  catches it through `onSubmit`. `preventDefault` keeps
                  the browser from navigating to the action URL (which
                  would be a same-origin GET reload). Also gives
                  password managers proper context for autofill. */}
              <form
                className={styles.fmSignupFields}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (step1Ready) handleAdvance();
                }}
              >
                <div className={styles.fmField}>
                  <div className={styles.fmFieldLabel}>
                    Email <span className={styles.fmFieldReq}>*</span>
                  </div>
                  <div className={styles.fmInputWrap}>
                    <input
                      type="email"
                      className={styles.fmInput}
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      data-cypress-id="guest-signup-email"
                    />
                  </div>
                  {errors.email && <div className={styles.fmFieldError}>{errors.email}</div>}
                </div>
                {/* Password + captcha only on the regular email/password
                    path. OAuth users have no password (Google proved
                    identity) and captcha gates password brute-force,
                    neither of which apply here. */}
                {!oauthData && (
                  <>
                    <div className={styles.fmField}>
                      <div className={styles.fmFieldLabel}>
                        Password <span className={styles.fmFieldReq}>*</span>
                      </div>
                      <div className={styles.fmInputWrap}>
                        <input
                          type={showPassword ? "text" : "password"}
                          className={styles.fmInput}
                          placeholder="••••••••"
                          style={{ paddingRight: 40 }}
                          value={form.password}
                          onChange={(e) => updateField("password", e.target.value)}
                          data-cypress-id="guest-signup-password"
                        />
                        <button
                          type="button"
                          className={styles.fmEyeBtn}
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          data-cypress-id="guest-signup-password-toggle"
                        >
                          <EyeOffIcon />
                        </button>
                      </div>
                    </div>
                    <Captcha
                      onChangeCaptcha={(value) => {
                        if (!isCaptchaRequired()) return;
                        setIsCaptchaSuccess(!!value);
                      }}
                    />
                  </>
                )}
                <button
                  type="submit"
                  className={`${styles.fmEmailCta} ${step1Ready ? styles.enabled : ""}`}
                  disabled={!step1Ready}
                  data-cypress-id="guest-signup-step1-continue"
                >
                  Continue
                </button>
                {/* Step tracker (signup only — login is single-step).
                    Sits below the primary action so the user reads
                    "Continue → progress" instead of "progress →
                    Continue", which de-emphasizes the indicator and
                    keeps the eye on the next action. */}
                <div className={styles.suStepDots} role="presentation" aria-hidden="true">
                  <span className={`${styles.suStepDot} ${step === 1 ? styles.suStepDotActive : ""}`} />
                  <span className={`${styles.suStepDot} ${step === 2 ? styles.suStepDotActive : ""}`} />
                </div>
              </form>

              <div className={styles.fmSignupBottom}>
                <div>
                  Already have an account?{" "}
                  <span
                    className={styles.fmBottomLink}
                    role="button"
                    tabIndex={0}
                    onClick={switchToLogin}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        switchToLogin();
                      }
                    }}
                    data-cypress-id="guest-signup-switch-to-login"
                  >
                    Login.
                  </span>
                </div>
                <div>
                  Want to work with us? Sign in as a{" "}
                  <span
                    className={styles.fmBottomLink}
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowBaWarning(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowBaWarning(true);
                      }
                    }}
                    data-cypress-id="guest-signup-brand-ambassador"
                  >
                    Brand Ambassador
                  </span>
                  .
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.su2RightTitle}>Tell us about you</div>
              <div className={styles.su2RightSub}>Just a few more details</div>

              {/* Same form pattern as step 1: implicit submit on Enter
                  from any field. `handleSubmit` is async so we don't
                  await it from `onSubmit` directly — the guard inside
                  `handleSubmit` handles `!step2Ready || submitting`
                  re-entrancy, and we mirror that guard here as
                  defense-in-depth even though `disabled` already
                  blocks the implicit submit when the button is
                  unavailable. */}
              <form
                className={styles.fmSignupFields}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (step2Ready && !submitting) handleSubmit();
                }}
              >
                <div className={styles.fmRow2}>
                  <div className={styles.fmField}>
                    <div className={styles.fmFieldLabel}>
                      First name <span className={styles.fmFieldReq}>*</span>
                    </div>
                    <input
                      type="text"
                      className={styles.fmInput}
                      placeholder="Jane"
                      value={form.firstName}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v && !ONLY_LETTERS.test(v)) return;
                        updateField("firstName", v);
                      }}
                      data-cypress-id="guest-signup-first-name"
                    />
                  </div>
                  <div className={styles.fmField}>
                    <div className={styles.fmFieldLabel}>
                      Last name <span className={styles.fmFieldReq}>*</span>
                    </div>
                    <input
                      type="text"
                      className={styles.fmInput}
                      placeholder="Doe"
                      value={form.lastName}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v && !ONLY_LETTERS.test(v)) return;
                        updateField("lastName", v);
                      }}
                      data-cypress-id="guest-signup-last-name"
                    />
                  </div>
                </div>

                <div className={styles.fmField}>
                  <div className={styles.fmFieldLabel}>
                    Country <span className={styles.fmFieldReq}>*</span>
                  </div>
                  <CountrySelect
                    value={form.countryId}
                    countries={countries}
                    onChange={(id) => updateField("countryId", id)}
                  />
                </div>

                <div className={styles.fmField}>
                  <div className={styles.fmFieldLabel}>
                    Phone number <span className={styles.fmFieldReq}>*</span>
                  </div>
                  <PhoneInput
                    country={"us"}
                    value={form.phone}
                    onChange={(value) => updateField("phone", value)}
                    inputClass={styles.fmInput}
                    inputStyle={{ width: "100%", height: 40, fontSize: 14 }}
                    buttonStyle={{ borderRadius: "8px 0 0 8px", border: "1.5px solid #e2e2fc" }}
                    containerStyle={{ width: "100%" }}
                    // `inputProps` is the only documented escape hatch for
                    // attaching attributes to the underlying <input> in
                    // react-phone-input-2 — Cypress targets it via this id.
                    inputProps={{ "data-cypress-id": "guest-signup-phone" }}
                  />
                </div>

                <div className={styles.fmField}>
                  <div className={styles.fmFieldLabel}>
                    Company name <span className={styles.fmFieldReq}>*</span>
                  </div>
                  <input
                    type="text"
                    className={styles.fmInput}
                    placeholder="Acme Inc."
                    value={form.companyName}
                    onChange={(e) => updateField("companyName", e.target.value)}
                    data-cypress-id="guest-signup-company-name"
                  />
                </div>

                {submitError && <div className={styles.fmFieldError}>{submitError}</div>}

                <button
                  type="submit"
                  className={`${styles.fmEmailCta} ${step2Ready && !submitting ? styles.enabled : ""}`}
                  disabled={!step2Ready || submitting}
                  data-cypress-id="guest-signup-submit"
                >
                  {submitting ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {/* Match the MUI spinner pattern the rest of the project uses
                          (see `components/SubmitButton`). `color="inherit"` makes
                          it adopt the button's text color so it stays legible on
                          both the enabled (white-on-pink) and disabled states. */}
                      <CircularProgress size={16} color="inherit" thickness={5} />
                      Creating account...
                    </span>
                  ) : (
                    "Create free account"
                  )}
                </button>

                <button
                  type="button"
                  className={styles.fmBackBtn}
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  data-cypress-id="guest-signup-back"
                >
                  ← Back
                </button>
                {/* Step tracker sits at the very bottom — below Back
                    too — so it stays at the same vertical position
                    across both steps (Step 1 has no Back button, so
                    its tracker is already the last form element). */}
                <div className={styles.suStepDots} role="presentation" aria-hidden="true">
                  <span className={`${styles.suStepDot} ${step === 1 ? styles.suStepDotActive : ""}`} />
                  <span className={`${styles.suStepDot} ${step === 2 ? styles.suStepDotActive : ""}`} />
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      {/* Brand Ambassador confirmation overlay. Rendered as a sibling
          of `.fmCard` so it sits ABOVE the signup card (the parent
          has a `z-index: 3001` on the overlay) without inheriting
          its `stopPropagation`. The user's design lives only in the
          guest Liveblocks room until they register — navigating away
          to oppizi.com discards it, hence the explicit confirm. */}
      {showBaWarning && (
        <div
          className={styles.baWarnOverlay}
          onClick={(e) => {
            // Clicking the backdrop dismisses the warning but does
            // NOT close the parent signup modal (the modal itself
            // already disables backdrop close, see the .fmCard
            // wrapper). `stopPropagation` keeps it that way.
            e.stopPropagation();
            setShowBaWarning(false);
          }}
        >
          <div className={styles.baWarnCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.baWarnTitle}>Leave Creative Studio?</div>
            <div className={styles.baWarnBody}>
              If you leave without creating an account, the design you&apos;ve started will be lost. Are you sure you
              want to continue to the Brand Ambassador page?
            </div>
            <div className={styles.baWarnActions}>
              <button
                type="button"
                className={styles.baWarnCancel}
                onClick={() => setShowBaWarning(false)}
                data-cypress-id="guest-signup-ba-cancel"
              >
                Stay here
              </button>
              <button
                type="button"
                className={styles.baWarnConfirm}
                onClick={() => {
                  // Same-tab navigation — popups would get blocked
                  // since this is a click handler one level removed
                  // from the original user gesture (the Brand
                  // Ambassador link), and we explicitly want the user
                  // to commit to leaving Creative Studio.
                  window.location.href = "https://oppizi.com/us/en/brand-ambassadors/";
                }}
                data-cypress-id="guest-signup-ba-confirm"
              >
                Yes, take me there
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupModal;
